import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, NavLink } from 'react-router-dom';
// import jsonData from '../../../features/sutta-title/components/SuttaTitle/index.json';
import './Header.css';

interface SuttaSearchResult {
  id: string;
  number: string;
  number_sc: string;
  title: string;
  title_pali: string;
  path?: string;
}

// Simple search that works reliably
const performSimpleSearch = (query: string, suttas: SuttaSearchResult[]): SuttaSearchResult[] => {
  const q = query.toLowerCase().replace(/\s/g, '');

  return suttas
    .filter(sutta => {
      if (sutta.number.toLowerCase().includes(q)) return true;
      if (sutta.title.toLowerCase().includes(q)) return true;
      if (sutta.title_pali?.toLowerCase().includes(q)) return true;
      if (sutta.number_sc?.toLowerCase().replace(/\s/g, '').includes(q)) return true;
      return false;
    })
    .slice(0, 10);
};

// Flatten JSON structure
const flattenSuttas = (data: any): SuttaSearchResult[] => {
  const flatten = (node: any): SuttaSearchResult[] => {
    if (Array.isArray(node) && node.length > 0 && node[0]?.number) {
      return node.map(sutta => ({ id: sutta.number, ...sutta }));
    }
    if (typeof node === 'object' && node !== null && !Array.isArray(node)) {
      return Object.keys(node)
        .filter(key => key !== 'title')
        .flatMap(key => flatten(node[key]));
    }
    return [];
  };
  return Object.values(data).flatMap(nikaya => flatten(nikaya));
};

const Header: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SuttaSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navLinksRef = useRef<HTMLUListElement>(null);

  const navigate = useNavigate();
  // const allSuttas = useMemo(() => flattenSuttas(jsonData), []); // <-- OLD (Sync)

  const [allSuttas, setAllSuttas] = useState<SuttaSearchResult[]>([]);

  // Lazy load search data to reduce TBT (280KB JSON + Processing)
  useEffect(() => {
    const loadData = async () => {
      // Small delay to ensure main thread is free for LCP/hydration
      await new Promise(resolve => setTimeout(resolve, 2000));

      try {
        const module =
          await import('../../../features/sutta-title/components/SuttaTitle/index.json');
        // Process in next tick
        setTimeout(() => {
          setAllSuttas(flattenSuttas(module.default));
        }, 0);
      } catch (err) {
        console.error('Failed to load search index', err);
      }
    };

    // Only load if not already loaded (though empty dependency array handles this)
    loadData();
  }, []);

  // --- HANDLER FUNCTIONS ---

  const performSearch = useCallback(
    (query: string) => {
      setIsSearching(true);
      const results = performSimpleSearch(query, allSuttas);
      setSearchResults(results);
      setIsSearching(false);
    },
    [allSuttas]
  );

  // --- HOOKS & LOGIC ---

  // Effect for auto-hiding header on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }
      lastScrollY.current = Math.max(0, currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Effect to close search results on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchResults([]);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Effect for debounced search
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    const timer = setTimeout(() => {
      performSearch(searchTerm.trim());
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, performSearch]);

  // Effect for search keyboard shortcut ('/')
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === '/' &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Effect for accessible focus management of the mobile menu
  useEffect(() => {
    if (isMenuOpen && navLinksRef.current) {
      const firstLink = navLinksRef.current.querySelector('a');
      firstLink?.focus();
    } else {
      menuButtonRef.current?.focus();
    }
  }, [isMenuOpen]);

  // --- HANDLER FUNCTIONS ---

  const handleResultClick = (sutta: SuttaSearchResult) => {
    // Use clean URL format: /sn1.1.8.8 instead of /sn1-1-8-8?path=...
    const urlPath = `/${sutta.number}`;
    setSearchTerm('');
    setSearchResults([]);
    setSelectedIndex(-1);
    navigate(urlPath);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (searchResults.length === 0 && e.key !== 'Escape') return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(p => (p < searchResults.length - 1 ? p + 1 : p));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(p => (p > 0 ? p - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleResultClick(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setSearchResults([]);
        setSearchTerm('');
        setSelectedIndex(-1);
        break;
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className={`app-header ${isHeaderVisible ? 'is-visible' : 'is-hidden'}`}>
      <nav className="container-fluid">
        <Link className="navbar-brand" to="/">
          <img
            src="/logo-96.png"
            className="navbar-brand-img"
            alt="ಕನ್ನಡ ತಿಪಿಟಕ ಲೋಗೋ"
            width="40"
            height="40"
          />
          <span>ಕನ್ನಡ ತಿಪಿಟಕ</span>
        </Link>

        <div id="tour-search-container" className="search-container" ref={searchRef}>
          <input
            ref={searchInputRef}
            type="search"
            className="form-control search-input"
            placeholder="Search sutta (or press `/`)"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setSelectedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            aria-label="Search suttas"
          />

          {isSearching && (
            <div className="search-results">
              <div className="search-loading">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Searching...</span>
                </div>
                <span className="ms-2">Searching...</span>
              </div>
            </div>
          )}

          {!isSearching && searchTerm && searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((sutta, index) => (
                <div
                  key={sutta.number}
                  className={`search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                  onMouseDown={() => {
                    handleResultClick(sutta);
                  }}
                  onMouseEnter={() => {
                    setSelectedIndex(index);
                  }}
                  role="option"
                  aria-selected={index === selectedIndex}
                >
                  <span className="sutta-number">{sutta.number}</span>
                  <span className="sutta-sc">({sutta.number_sc})</span>
                  <span className="sutta-title">{sutta.title}</span>
                </div>
              ))}
            </div>
          )}

          {!isSearching && searchTerm && searchTerm.length >= 2 && searchResults.length === 0 && (
            <div className="search-results">
              <div className="search-no-results">
                <p>No suttas found matching "{searchTerm}"</p>
              </div>
            </div>
          )}
        </div>

        <button
          ref={menuButtonRef}
          className={`hamburger-menu ${isMenuOpen ? 'is-active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation"
          aria-expanded={isMenuOpen}
          aria-controls="main-nav-links"
        >
          <span className="hamburger-box">
            <span className="hamburger-inner"></span>
          </span>
        </button>

        <ul
          id="main-nav-links"
          ref={navLinksRef}
          className={`navbar-nav-links ${isMenuOpen ? 'is-open' : ''}`}
        >
          <li>
            <NavLink to="/dn" onClick={handleNavClick}>
              ದೀಘನಿಕಾಯ
            </NavLink>
          </li>
          <li>
            <NavLink to="/mn" onClick={handleNavClick}>
              ಮಜ್ಝಿಮನಿಕಾಯ
            </NavLink>
          </li>
          <li>
            <NavLink to="/sn" onClick={handleNavClick}>
              ಸಂಯುತ್ತನಿಕಾಯ
            </NavLink>
          </li>
          <li>
            <NavLink to="/books" onClick={handleNavClick}>
              ಪುಸ್ತಕಗಳು
            </NavLink>
          </li>
          <li>
            <NavLink to="/locations" onClick={handleNavClick}>
              ಸ್ಥಳಗಳು
            </NavLink>
          </li>
          <li>
            <NavLink to="/interactive-dhamma" onClick={handleNavClick}>
              Interactive Dhamma
            </NavLink>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header;
